import { ApiClientError } from 'grabbitbot-legacy-ui'

/**
 * Best-effort extraction of the backend's real validation message out of an
 * `ApiClientError`.
 *
 * `grabbitbot-legacy-ui`'s own `parseErrorBody` (`api-client/errors.ts`)
 * recognizes two error-body shapes: `{error: <string>, message}` (legacy PHP)
 * and `{detail: string | {code, message}}` (a bare FastAPI default). This
 * screener's backend (`api.cryptopairscreener.com`, via
 * `grabbitbot_legacy_core.http.errors.install_exception_handlers`) uses a
 * THIRD shape for every momentum-store `ValidationFailed` (422) — unknown
 * enum values, malformed operands/pairs, and — the case this ticket cares
 * about — webhook validation (`credentials_in_url`, `unknown_method`, an
 * invalid URL):
 *
 *     {"error": {"code": 422, "message": "webhook.url: credentials_in_url: ..."}}
 *
 * Against that shape neither of `parseErrorBody`'s branches match (`error` is
 * an object, not a string; there is no `detail`), so it falls through to the
 * generic `Request failed (422)` — silently swallowing exactly the message
 * this page needs to show. Filed as a cross-cutting bug against
 * `grabbitbot-legacy-ui` (every consumer of this backend's error envelope is
 * affected, not just this page) — this reads the parsed body straight off
 * `ApiClientError.raw` as a screener-local workaround, since this app can't
 * patch the vendored library from here.
 */
export function extractApiErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiClientError) {
    const raw = err.raw
    if (raw && typeof raw === 'object' && 'error' in raw) {
      const inner = (raw as { error?: unknown }).error
      if (inner && typeof inner === 'object' && typeof (inner as { message?: unknown }).message === 'string') {
        return (inner as { message: string }).message
      }
    }
    if (err.message) return err.message
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}
